import type { StoreState } from '@deqo/foundation';
import { Burden, Destructor, memone, VoidCallback } from '@deqo/foundation';
import getStroke, { StrokeOptions } from 'perfect-freehand';
import { ObjectManager } from '../../../concepts/objects';
import { Point } from '../../../platform/geom/Point';
import { UniversalSurfaceRenderer } from '../../../platform/UniversalSurfaceRenderer';
import { Camera } from '../../canvas/Camera';
import { EditorSession } from '../../canvas/EditorSession';
import { OverlayManager } from '../../canvas/overlay/OverlayManager';
import { ActiveObject, resolve } from '../../Container';
import { Annotation } from './Annotation';
import { AnnotationStore } from './AnnotationStore';

class AnnotationDraw {

    constructor(private annotation: Annotation, public ticket: number) {
    }

    public get id() {
        return this.annotation.id;
    }

    public get color() {
        return this.annotation.color;
    }

    public get alpha() {
        const t = (new Date()).getTime();
        return Math.max(0, Math.min(1, ((this.annotation.until - t) / Annotation.ttl) * 1.2));
    }

    public path(scale: number) {
        return this.computePath(scale, this.annotation.data);
    }

    @memone()
    private computePath(scale: number, data: number[]) {
        if (data.length < 4) return new Path2D('Z');

        const pairs = [] as [number, number][];
        for (let i = 1; i < data.length; i += 2) {
            pairs.push([data[i - 1], data[i]]);
        }

        const stroke = getStrokeAsPoints(pairs, {
            size: 10 / scale,
            smoothing: 0.6,
            thinning: 0.15,
            streamline: 0.6,
            easing: (t) => t,
            start: {
                taper: 0,
                cap: true,
            },
            end: {
                taper: 0,
                cap: true,
            },
        });

        const path = stroke.reduce(
            (acc, p0, i, arr) => {
                const p1 = arr[(i + 1) % arr.length];
                acc.push(p0.x, p0.y, (p0.x + p1.x) / 2, (p0.y + p1.y) / 2);
                return acc;
            },
            ['M', ...stroke[0].toArray(), 'Q']
        );

        path.push('Z');
        return new Path2D(path.join(' '));
    }
}

const watchStore = (store: AnnotationStore, callback: VoidCallback) => {

    const children = [] as Destructor[];
    const parent = store.observe((next) => {
        Burden.resolveAll(children);
        children.splice(0, children.length, ...next.map(x => x.observe(() => callback())));
        callback();
    });

    return () => Burden.resolveAll([parent, ...children]);
};


export class AnnotationOverlay extends ActiveObject {

    @resolve(Camera) private camera!: Camera;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(OverlayManager) private overlays!: OverlayManager;
    @resolve(AnnotationStore) private store!: AnnotationStore;

    private counter = 0;
    private pool = new Map<string, AnnotationDraw>();

    public start() {

        const overlay = this.overlays.createOverlay('annotations', gfx => {
            if (this.paint(gfx)) {
                overlay.invalidate();
            }
        });

        this.overlays.add(overlay);

        this.burden(
            overlay,
            this.camera.observe(() => void (this.store.count && overlay.invalidate())),
            watchStore(this.store, () => overlay.invalidate()),
        );
    }

    private paint(gfx: UniversalSurfaceRenderer) {

        const ticket = ++this.counter;
        const list = this.getSortedDrawList(this.store.getState());

        for (const draw of list) {
            gfx.setTransform(this.camera.modelViewMatrix);
            gfx.fillStyle = draw.color;
            gfx.globalAlpha = draw.alpha;
            gfx.fill(draw.path(this.camera.scale));
            draw.ticket = ticket;
        }

        for (const draw of Array.from(this.pool.values())) {
            if (draw.ticket !== ticket) {
                this.pool.delete(draw.id);
            }
        }

        return list.length > 0;
    }

    @memone()
    private getSortedDrawList(items: StoreState<AnnotationStore>) {
        return Array.from(items)
            .sort((a, b) => a.sort - b.sort)
            .map(x => this.resolveDraw(x));
    }

    private resolveDraw(item: Annotation) {
        if (!this.pool.has(item.id)) {
            this.pool.set(item.id, new AnnotationDraw(item, this.counter));
        }
        return this.pool.get(item.id);
    }
}

const getStrokeAsPoints = (pairs: [number, number][], options?: StrokeOptions) => getStroke(pairs, options).map(x => Point.from(x));
